/**
 * Migration Utilities
 * 
 * Helper functions for migrating from localStorage to the database-backed
 * on-the-fly card generation system.
 */

import scratchCardService from '../services/scratchCardService';
import { logInfo, logError, logSuccess } from './logger.js';

/**
 * Extract game data from localStorage
 * @returns {Array} Array of game data objects
 */
export const extractGamesFromLocalStorage = () => {
  try {
    // Get platform games
    const platformGames = JSON.parse(localStorage.getItem('palmheist_games') || '[]');
    
    // Get user-created games
    const userGameIds = JSON.parse(localStorage.getItem('userCreatedGames') || '[]');
    const userGames = userGameIds.map(gameId => {
      const gameData = JSON.parse(localStorage.getItem(`game_${gameId}`) || 'null');
      return gameData;
    }).filter(Boolean);
    
    return [...platformGames, ...userGames];
  } catch (error) {
    logError('Error extracting games from localStorage:', error);
    return [];
  }
};

/**
 * Extract batch data from localStorage
 * @returns {Array} Array of batch data objects
 */
export const extractBatchesFromLocalStorage = () => {
  try {
    const batchKeys = Object.keys(localStorage).filter(key => key.startsWith('batch_'));
    const batches = batchKeys.map(key => {
      const batchData = JSON.parse(localStorage.getItem(key) || 'null');
      return batchData;
    }).filter(Boolean);
    
    return batches;
  } catch (error) {
    logError('Error extracting batches from localStorage:', error);
    return [];
  }
};

/**
 * Extract card data from localStorage
 * @returns {Array} Array of card data objects
 */
export const extractCardsFromLocalStorage = () => {
  try {
    const cardKeys = Object.keys(localStorage).filter(key => key.startsWith('card_'));
    const cards = cardKeys.map(key => {
      const cardData = JSON.parse(localStorage.getItem(key) || 'null');
      return cardData;
    }).filter(Boolean);
    
    return cards;
  } catch (error) {
    logError('Error extracting cards from localStorage:', error);
    return [];
  }
};

/**
 * Extract lottery ticket data from localStorage
 * @returns {Array} Array of lottery ticket data objects
 */
export const extractLotteryTicketsFromLocalStorage = () => {
  try {
    const ticketKeys = Object.keys(localStorage).filter(key => key.startsWith('lottery_ticket_'));
    const tickets = ticketKeys.map(key => {
      const ticketData = JSON.parse(localStorage.getItem(key) || 'null');
      return ticketData;
    }).filter(Boolean);
    
    return tickets;
  } catch (error) {
    logError('Error extracting lottery tickets from localStorage:', error);
    return [];
  }
};

/**
 * Extract lottery draw data from localStorage
 * @returns {Array} Array of lottery draw data objects
 */
export const extractLotteryDrawsFromLocalStorage = () => {
  try {
    const drawKeys = Object.keys(localStorage).filter(key => key.startsWith('lottery_draw_'));
    const draws = drawKeys.map(key => {
      const drawData = JSON.parse(localStorage.getItem(key) || 'null');
      return drawData;
    }).filter(Boolean);
    
    return draws;
  } catch (error) {
    logError('Error extracting lottery draws from localStorage:', error);
    return [];
  }
};

/**
 * Migrate all data from localStorage to the backend
 * @returns {Promise<Object>} Migration results
 */
export const migrateAllData = async () => {
  try {
    logInfo('Starting migration from localStorage to backend...');
    
    // Extract data from localStorage
    const games = extractGamesFromLocalStorage();
    const batches = extractBatchesFromLocalStorage();
    const cards = extractCardsFromLocalStorage();
    const tickets = extractLotteryTicketsFromLocalStorage();
    const draws = extractLotteryDrawsFromLocalStorage();
    
    // Send data to backend for migration
    const response = await fetch('/api/scratch-cards/migrate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        games,
        batches,
        cards,
        tickets,
        draws
      })
    });
    
    if (!response.ok) {
      throw new Error(`Migration failed with status: ${response.status}`);
    }
    
    const result = await response.json();
    logSuccess('Migration completed successfully:', result);
    
    return result;
  } catch (error) {
    logError('Migration error:', error);
    throw error;
  }
};

/**
 * Check if a user has data that needs migration
 * @returns {boolean} True if migration is needed
 */
export const checkIfMigrationNeeded = () => {
  try {
    // Check if there's any scratch card related data in localStorage
    const hasGames = localStorage.getItem('palmheist_games') !== null;
    const hasUserGames = localStorage.getItem('userCreatedGames') !== null;
    const hasBatches = Object.keys(localStorage).some(key => key.startsWith('batch_'));
    const hasCards = Object.keys(localStorage).some(key => key.startsWith('card_'));
    
    return hasGames || hasUserGames || hasBatches || hasCards;
  } catch (error) {
    logError('Error checking if migration is needed:', error);
    return false;
  }
};

/**
 * Clear localStorage data after successful migration
 * @param {boolean} keepBackup - Whether to keep a backup of the data
 */
export const clearLocalStorageAfterMigration = (keepBackup = true) => {
  try {
    if (keepBackup) {
      // Create a backup of all localStorage data
      const backup = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        backup[key] = localStorage.getItem(key);
      }
      
      // Store the backup
      localStorage.setItem('palmheist_migration_backup', JSON.stringify(backup));
      logInfo('Created backup of localStorage data before clearing');
    }
    
    // Clear only scratch card related data
    const keysToRemove = [];
    
    // Find all keys to remove
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key === 'palmheist_games' ||
        key === 'userCreatedGames' ||
        key.startsWith('game_') ||
        key.startsWith('batch_') ||
        key.startsWith('card_') ||
        key.startsWith('lottery_ticket_') ||
        key.startsWith('lottery_draw_')
      ) {
        keysToRemove.push(key);
      }
    }
    
    // Remove the keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    logSuccess(`Cleared ${keysToRemove.length} items from localStorage after migration`);
  } catch (error) {
    logError('Error clearing localStorage after migration:', error);
  }
};

/**
 * Restore localStorage data from backup
 * @returns {boolean} True if restoration was successful
 */
export const restoreFromBackup = () => {
  try {
    const backupData = localStorage.getItem('palmheist_migration_backup');
    
    if (!backupData) {
      logError('No backup data found in localStorage');
      return false;
    }
    
    const backup = JSON.parse(backupData);
    
    // Restore all data from backup
    Object.keys(backup).forEach(key => {
      localStorage.setItem(key, backup[key]);
    });
    
    logSuccess(`Restored ${Object.keys(backup).length} items from backup`);
    return true;
  } catch (error) {
    logError('Error restoring from backup:', error);
    return false;
  }
};
